import React from 'react';
import { Search, Filter } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

type Props = {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  organizerFilter: string;
  setOrganizerFilter: (value: string) => void;
  hotelFilter: string;
  setHotelFilter: (value: string) => void;
  organizers: Record<string, any>;
  hotels: string[];
  userRole: string;
};

export const RequestFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  organizerFilter,
  setOrganizerFilter,
  hotelFilter,
  setHotelFilter,
  organizers,
  hotels,
  userRole
}: Props) => {
  const { translations } = useLanguage();

  const hasActiveFilters = searchTerm || 
    statusFilter !== 'all' || 
    organizerFilter !== 'all' ||
    hotelFilter !== 'all';

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-lg shadow-sm space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder={translations?.roomUpgrade?.searchPlaceholder || "Search by pilgrim name or passport..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="payment_pending">Payment Pending</option>
          <option value="paid">Paid</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>

        {/* Hotel Filter */}
        <select
          value={hotelFilter}
          onChange={(e) => setHotelFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Hotels</option>
          {hotels.map((hotel) => (
            <option key={hotel} value={hotel}>
              {hotel}
            </option>
          ))}
        </select>

        {/* Organizer Filter - Only for admin */}
        {userRole === 'admin' && (
          <select
            value={organizerFilter}
            onChange={(e) => setOrganizerFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Organizers</option>
            {Object.values(organizers).map((organizer: any) => (
              <option key={organizer.id} value={organizer.id}>
                {organizer.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="flex items-center gap-2 pt-2"
        >
          <Filter size={16} className="text-primary-500" />
          <span className="text-sm text-gray-600">Active Filters:</span>
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                Search: {searchTerm}
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                Status: {statusFilter}
              </span>
            )}
            {hotelFilter !== 'all' && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                Hotel: {hotelFilter}
              </span>
            )}
            {organizerFilter !== 'all' && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                Organizer: {organizers[organizerFilter]?.name}
              </span>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};